import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '../shared/SearchableSelect';
import type { TransmitterFormData, TransmitterOptions } from '../../types';

interface ConnectivitySectionProps {
  formData: TransmitterFormData;
  setField: <K extends keyof TransmitterFormData>(key: K, value: TransmitterFormData[K]) => void;
  options: TransmitterOptions;
}

export function ConnectivitySection({
  formData,
  setField,
  options,
}: ConnectivitySectionProps) {
  // hardware_radio stores the hex_id from hardware_specs.i with '0x' prefix.
  // 0x10 = XBee Zigbee (shows XBee serial field), 0x20 = XBee LTE cellular (shows SIM/ICCID/IMEI).
  const isCellular = formData.hardware_radio === '0x20';
  const isXBee = formData.hardware_radio === '0x10';

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Connectivity Specifications
      </h3>

      {/* Key */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Key</Label>
        <div className="flex items-center gap-2">
          <SearchableSelect
            value={formData.key}
            onValueChange={(val) => setField('key', val)}
            options={options.keys.map((k) => ({ ...k, name: k.name || k._id }))}
            placeholder="Select key"
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground shrink-0">
            The organisation key registered in the transmitter.
          </span>
        </div>
      </div>

      {/* XBee Serial (editable — only when connectivity = XBee) */}
      {isXBee && (
        <div className="grid grid-cols-[140px_1fr] items-center gap-2">
          <Label>XBee Serial</Label>
          <Input
            value={formData.xbee_SL}
            onChange={(e) => setField('xbee_SL', e.target.value)}
          />
        </div>
      )}

      {/* SIM Provider */}
      {isCellular && (
        <div className="grid grid-cols-[140px_1fr] items-center gap-2">
          <Label>SIM Provider</Label>
          <Input
            value={formData.simprovider}
            onChange={(e) => setField('simprovider', e.target.value)}
          />
        </div>
      )}

      {/* ICCID */}
      {isCellular && (
        <div className="grid grid-cols-[140px_1fr] items-center gap-2">
          <Label>ICCID</Label>
          <Input
            value={formData.ICCID}
            onChange={(e) => setField('ICCID', e.target.value)}
          />
        </div>
      )}

      {/* IMEI */}
      {isCellular && (
        <div className="grid grid-cols-[140px_1fr] items-center gap-2">
          <Label>IMEI</Label>
          <Input
            value={formData.IMEI}
            onChange={(e) => setField('IMEI', e.target.value)}
          />
        </div>
      )}
    </div>
  );
}
